import { Node } from "@tiptap/core"
import { Plugin, PluginKey } from "@tiptap/pm/state"

import { hasBlockLikeDisplay } from "@/tiptap/core/dom-layout"
import { normalizeFirstPartyImageSrc } from "@/tiptap/core/editor-clipboard"
import {
  isEmojiFallbackImage,
  parseImageAttributes,
} from "@/tiptap/nodes/image-node/image-node-extension"

const BLOCK_SIBLING_TAGS = new Set([
  "P", "H1", "H2", "H3", "H4", "H5", "H6",
  "DIV", "FIGURE", "DETAILS", "UL", "OL", "TABLE", "BLOCKQUOTE",
  "PRE", "HR", "SECTION", "ARTICLE",
])

// A <br> or a block-styled inline element ends the visual line.
function isLineBoundary(node) {
  return node instanceof Element && (node.tagName === "BR" || hasBlockLikeDisplay(node))
}

function isNonWhitespaceLeaf(node) {
  if (node.nodeType === 3) return Boolean(node.textContent?.trim())
  if (!(node instanceof Element)) return false
  if (isLineBoundary(node) || BLOCK_SIBLING_TAGS.has(node.tagName)) return false
  return Boolean(node.textContent?.trim())
}

// Wrappers that text, breaks and images can share without leaving the line.
const INLINE_WRAPPER_TAGS = new Set([
  "SPAN", "A", "STRONG", "B", "EM", "I", "U", "S", "STRIKE", "CODE", "FONT",
  "SUB", "SUP", "MARK", "SMALL", "KBD", "CITE", "DFN", "INS", "DEL", "LABEL",
])

function isInlineWrapper(node) {
  return (
    node instanceof Element &&
    INLINE_WRAPPER_TAGS.has(node.tagName) &&
    !hasBlockLikeDisplay(node)
  )
}

function enclosingTextblock(img) {
  let node = img.parentNode
  while (isInlineWrapper(node)) node = node.parentNode
  return node
}

function flattenInlineLeaves(root, leaves = []) {
  for (const child of root.childNodes) {
    if (isInlineWrapper(child)) flattenInlineLeaves(child, leaves)
    else leaves.push(child)
  }

  return leaves
}

// A <br> nested in a formatting wrapper still breaks the paragraph's line, so
// the image's line has to be read from the whole textblock rather than from
// its immediate siblings.
function lineAroundImage(img) {
  const block = enclosingTextblock(img)
  if (!block?.childNodes) return null

  const leaves = flattenInlineLeaves(block)
  const index = leaves.indexOf(img)
  if (index === -1) return null

  let start = index
  while (start > 0 && !isLineBoundary(leaves[start - 1])) start -= 1

  let end = index
  while (end < leaves.length - 1 && !isLineBoundary(leaves[end + 1])) end += 1

  return { leaves, index, start, end }
}

// ScreenCapture HTML is `<p>Step text<br><img></p>`, leaving the image alone on
// its line. That makes it a block screenshot rather than an icon in a text run.
function sharesLineWithContent(line) {
  if (!line) return false

  for (let i = line.start; i <= line.end; i += 1) {
    if (i === line.index) continue
    if (isNonWhitespaceLeaf(line.leaves[i])) return true
  }

  return false
}

// The walk above only stops short of the start at a line boundary, so a line
// that does not begin the textblock is a line the author broke into.
function followsLineBoundary(line) {
  return Boolean(line) && line.start > 0
}

const SIZE_DECLARATION = /(?:^|[;\s])(?:max-|min-)?(?:width|height)\s*:/i

// Mirrors the CSS that shrinks inline images to icon height, which skips any
// image sized by attribute or by style.
function hasExplicitSize(img) {
  if (img.getAttribute("width") || img.getAttribute("height")) return true

  const style = img.getAttribute("style") || img.getAttribute("data-mce-style") || ""
  return SIZE_DECLARATION.test(style)
}

export function shouldParseAsInlineImage(img) {
  const line = lineAroundImage(img)
  if (sharesLineWithContent(line)) return true
  if (img.getAttribute("data-inline") !== "true") return false
  // An unsized data-inline image stranded on its own line after a break is a
  // screenshot 2.47 mis-tagged as an icon; demote it so it heals on save.
  return hasExplicitSize(img) || !followsLineBoundary(line)
}

const INLINE_IMAGE_RESIZE_KEY = new PluginKey("inlineImageResize")
const RESIZE_EDGE_PX = 8
const MIN_INLINE_IMAGE_SIZE = 16

export function resizeEdgeAt(rect, clientX, clientY, threshold = RESIZE_EDGE_PX) {
  const left = clientX - rect.left <= threshold
  const right = rect.right - clientX <= threshold
  const top = clientY - rect.top <= threshold
  const bottom = rect.bottom - clientY <= threshold
  if (!left && !right && !top && !bottom) return null

  return { left, right, top, bottom }
}

export function cursorForResizeEdge(edge) {
  if (!edge) return null

  const { left, right, top, bottom } = edge
  if ((left || right) && (top || bottom)) {
    return (left && top) || (right && bottom) ? "nwse-resize" : "nesw-resize"
  }
  if (left || right) return "ew-resize"
  return "ns-resize"
}

export function sizeFromResize({ edge, start, current, minSize = MIN_INLINE_IMAGE_SIZE }) {
  let width = start.width
  let height = start.height
  const dx = current.x - start.x
  const dy = current.y - start.y

  if (edge.right) width = start.width + dx
  if (edge.left) width = start.width - dx
  if (edge.bottom) height = start.height + dy
  if (edge.top) height = start.height - dy

  if (start.width > 0 && start.height > 0) {
    if ((edge.left || edge.right) && !(edge.top || edge.bottom)) {
      height = start.height * (width / start.width)
    } else if ((edge.top || edge.bottom) && !(edge.left || edge.right)) {
      width = start.width * (height / start.height)
    } else {
      height = start.height * (width / start.width)
    }

    const scale = Math.max(
      width / start.width,
      minSize / start.width,
      minSize / start.height,
    )
    return {
      width: Math.round(start.width * scale),
      height: Math.round(start.height * scale),
    }
  }

  return {
    width: Math.max(minSize, Math.round(width)),
    height: Math.max(minSize, Math.round(height)),
  }
}

const RESIZE_CURSOR_CLASSES = [
  "is-resize-ew",
  "is-resize-ns",
  "is-resize-nwse",
  "is-resize-nesw",
]

const RESIZE_CURSOR_CLASS_BY_CURSOR = {
  "ew-resize": "is-resize-ew",
  "ns-resize": "is-resize-ns",
  "nwse-resize": "is-resize-nwse",
  "nesw-resize": "is-resize-nesw",
}

function applyInlineImageSize(img, width, height) {
  img.setAttribute("width", String(width))
  img.setAttribute("height", String(height))
  img.style.width = `${width}px`
  img.style.height = `${height}px`
}

function setResizeCursorClass(img, cursor) {
  img.classList.remove(...RESIZE_CURSOR_CLASSES)
  const className = RESIZE_CURSOR_CLASS_BY_CURSOR[cursor]
  if (className) img.classList.add(className)
}

function attrsForInlineImage(img) {
  if (!(img instanceof Element)) return false
  if (isEmojiFallbackImage(img)) return false
  if (img.closest("figure")) return false

  return parseImageAttributes(img)
}

export const InlineImage = Node.create({
  name: "inlineImage",
  inline: true,
  group: "inline",
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => normalizeFirstPartyImageSrc(element.getAttribute("src")),
      },
      alt: { default: null },
      title: { default: null },
      width: { default: null },
      height: { default: null },
      style: { default: null },
    }
  },

  parseHTML() {
    return [
      {
        tag: "img[src]",
        priority: 60,
        getAttrs: (img) => {
          const attrs = attrsForInlineImage(img)
          if (!attrs || !shouldParseAsInlineImage(img)) return false
          return attrs
        },
      },
    ]
  },

  renderHTML({ node }) {
    const imgAttrs = {
      src: node.attrs.src || "",
      "data-inline": "true",
    }

    if (node.attrs.alt) imgAttrs.alt = node.attrs.alt
    if (node.attrs.title) imgAttrs.title = node.attrs.title
    if (node.attrs.width) imgAttrs.width = node.attrs.width
    if (node.attrs.height) imgAttrs.height = node.attrs.height
    if (node.attrs.style) imgAttrs.style = node.attrs.style

    return ["img", imgAttrs]
  },

  addProseMirrorPlugins() {
    const imageType = this.type
    let drag = null

    const commitResize = (view) => {
      if (!drag) return

      const { pos, width, height, img } = drag
      drag = null
      setResizeCursorClass(img, null)

      const node = view.state.doc.nodeAt(pos)
      if (!node || node.type !== imageType) return

      view.dispatch(
        view.state.tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          width,
          height,
        }),
      )
    }

    return [
      new Plugin({
        key: INLINE_IMAGE_RESIZE_KEY,
        view(editorView) {
          const onMove = (event) => {
            if (!drag) return

            const size = sizeFromResize({
              edge: drag.edge,
              start: drag.start,
              current: { x: event.clientX, y: event.clientY },
            })
            drag.width = size.width
            drag.height = size.height
            applyInlineImageSize(drag.img, size.width, size.height)
          }

          const onUp = () => commitResize(editorView)

          window.addEventListener("mousemove", onMove)
          window.addEventListener("mouseup", onUp)
          return {
            destroy() {
              window.removeEventListener("mousemove", onMove)
              window.removeEventListener("mouseup", onUp)
            },
          }
        },
        props: {
          handleDOMEvents: {
            mousedown(view, event) {
              if (!view.editable || event.button !== 0) return false

              const img = event.target
              if (!(img instanceof HTMLImageElement)) return false
              if (img.getAttribute("data-inline") !== "true") return false
              if (!img.classList.contains("ProseMirror-selectednode")) return false

              const rect = img.getBoundingClientRect()
              const edge = resizeEdgeAt(rect, event.clientX, event.clientY)
              if (!edge) return false

              let pos
              try {
                pos = view.posAtDOM(img, 0)
              } catch {
                return false
              }

              event.preventDefault()
              drag = {
                pos,
                img,
                edge,
                start: {
                  x: event.clientX,
                  y: event.clientY,
                  width: rect.width,
                  height: rect.height,
                },
                width: Math.round(rect.width),
                height: Math.round(rect.height),
              }
              return true
            },
            mousemove(_view, event) {
              if (drag) return false

              const img = event.target
              if (!(img instanceof HTMLImageElement)) return false
              if (img.getAttribute("data-inline") !== "true") return false
              if (!img.classList.contains("ProseMirror-selectednode")) {
                setResizeCursorClass(img, null)
                return false
              }

              const edge = resizeEdgeAt(img.getBoundingClientRect(), event.clientX, event.clientY)
              setResizeCursorClass(img, cursorForResizeEdge(edge))
              return false
            },
            mouseleave(_view, event) {
              const img = event.target
              if (!(img instanceof HTMLImageElement)) return false
              if (!drag) setResizeCursorClass(img, null)
              return false
            },
          },
        },
      }),
    ]
  },
})

export default InlineImage
